import {
  FormPageProperty,
  FormPageInputItem,
  INPUT_ITEM_TYPE,
  MailAddressPropertyAtSignSeparated,
  MailAddressPropertyDomainPulldown,
  TextPropertyName,
  TextPropertyTel,
  TextPropertyZipCode,
  FormPageAuthProperty,
  FormPageAuthInputItem,
  AUTH_INPUT_ITEM_TYPE,
} from 'shared';
import {
  SingleSelectionValueForValidation,
  MultipleSelectionValueForValidation,
} from '../shared';
import {
  convertAuthDateFree,
  convertDateFree,
  convertDateYearPulldown,
} from './date';
import { convertFileUploadPiclib } from './fileUpload';
import {
  convertAuthMailAddressFree,
  convertMailAddressAtSignSeparated,
  convertMailAddressDomainPulldown,
  convertMailAddressFree,
} from './mailAddress';
import {
  convertAuthMonthDayFree,
  convertMonthDayFree,
  convertMonthDayPulldown,
} from './monthDay';
import { convertMultipleSelectionCheckbox } from './multipleSelection';
import { convertAuthNumberFree, convertNumberAdditionalText } from './number';
import {
  convertAuthPasswordHashFree,
  convertPasswordHashText,
} from './passwordHash';
import {
  convertSingleSelectionPulldown,
  convertSingleSelectionRadio,
} from './singleSelection';
import { convertAuthSynergyIdFree } from './synergyId';
import {
  convertAuthTextFree,
  convertTextAdditionalText,
  convertTextMultiLine,
  convertTextName,
  convertTextSingleLine,
  convertTextTel,
  convertTextZipCode,
  convertTextZipCodeWithoutHyphen,
} from './text';

const assertNever = (v: never) => {
  console.error(`${v} is unknown type`);
};

type ConvertValueReturnType =
  | string
  | undefined
  | SingleSelectionValueForValidation
  | MultipleSelectionValueForValidation
  | boolean;

const converter = (
  property: FormPageProperty,
  value: FormPageInputItem
): ConvertValueReturnType => {
  switch (value.inputItemType) {
    case INPUT_ITEM_TYPE.mailAddressFree:
      return convertMailAddressFree(value);
    case INPUT_ITEM_TYPE.mailAddressAtSignSeparated:
      return convertMailAddressAtSignSeparated(
        value,
        property as MailAddressPropertyAtSignSeparated
      );
    case INPUT_ITEM_TYPE.mailAddressDomainPulldown:
      return convertMailAddressDomainPulldown(
        value,
        property as MailAddressPropertyDomainPulldown
      );
    case INPUT_ITEM_TYPE.textSingleLine:
      return convertTextSingleLine(value);
    case INPUT_ITEM_TYPE.textName:
      return convertTextName(value, property as TextPropertyName);
    case INPUT_ITEM_TYPE.textTel:
      return convertTextTel(value, property as TextPropertyTel);
    case INPUT_ITEM_TYPE.textZipCode:
      return convertTextZipCode(value, property as TextPropertyZipCode);
    case INPUT_ITEM_TYPE.textZipCodeWithoutHyphen:
      return convertTextZipCodeWithoutHyphen(value);
    case INPUT_ITEM_TYPE.textAdditionalText:
      return convertTextAdditionalText(value);
    case INPUT_ITEM_TYPE.textMultiLine:
      return convertTextMultiLine(value);
    case INPUT_ITEM_TYPE.numberAdditionalText:
      return convertNumberAdditionalText(value);
    case INPUT_ITEM_TYPE.dateFree:
      return convertDateFree(value);
    case INPUT_ITEM_TYPE.dateYearPulldown:
      return convertDateYearPulldown(value);
    case INPUT_ITEM_TYPE.monthDayFree:
      return convertMonthDayFree(value);
    case INPUT_ITEM_TYPE.monthDayPulldown:
      return convertMonthDayPulldown(value);
    case INPUT_ITEM_TYPE.singleSelectionPulldown:
      return convertSingleSelectionPulldown(value);
    case INPUT_ITEM_TYPE.singleSelectionRadio:
      return convertSingleSelectionRadio(value);
    case INPUT_ITEM_TYPE.multipleSelectionCheckbox:
      return convertMultipleSelectionCheckbox(value);
    case INPUT_ITEM_TYPE.mailRefusalFlagRadio:
      return value.isRefused ?? undefined;
    case INPUT_ITEM_TYPE.passwordHashText:
      return convertPasswordHashText(value);
    case INPUT_ITEM_TYPE.fileUploadPiclib:
      return convertFileUploadPiclib(value);
    default: {
      assertNever(value);
    }
  }
};

const converterAuth = (
  value: FormPageAuthInputItem
): ConvertValueReturnType => {
  switch (value.authenticationInputItemType) {
    case AUTH_INPUT_ITEM_TYPE.synergyIdFree:
      return convertAuthSynergyIdFree(value);
    case AUTH_INPUT_ITEM_TYPE.mailAddressFree:
      return convertAuthMailAddressFree(value);
    case AUTH_INPUT_ITEM_TYPE.textFree:
      return convertAuthTextFree(value);
    case AUTH_INPUT_ITEM_TYPE.numberFree:
      return convertAuthNumberFree(value);
    case AUTH_INPUT_ITEM_TYPE.dateFree:
      return convertAuthDateFree(value);
    case AUTH_INPUT_ITEM_TYPE.monthDayFree:
      return convertAuthMonthDayFree(value);
    case AUTH_INPUT_ITEM_TYPE.passwordHashFree:
      return convertAuthPasswordHashFree(value);
    default: {
      assertNever(value);
    }
  }
};

export const convertValue = (
  property: FormPageProperty | FormPageAuthProperty,
  value: FormPageInputItem | FormPageAuthInputItem
): ConvertValueReturnType => {
  if ('inputItemType' in value) {
    return converter(property as FormPageProperty, value);
  }
  return converterAuth(value);
};
